// Likes and play counts for community builds and sound tests.
// Every [data-like-entry] button and [data-plays-entry] label on the page is kept in sync, including
// ones added later (like the build viewer's details panel). Counts are stored by the community service.
(() => {
  if (window.kkCommunityStats) return;

  const script = document.currentScript;
  const api = (script?.dataset.api || '').replace(/\/+$/, '');
  if (!api) return;

  const PLAYS_SHOWN_FROM = 10; // a brand-new video doesn't advertise "1 play"
  const state = {}; // entry id -> { likes, plays, liked }
  const requested = new Set();
  let fallbackVisitor = null;

  // A random ID kept in this browser, so a like or play is only counted once. Nothing personal.
  function visitorId() {
    try {
      let id = localStorage.getItem('kk-visitor');
      if (!id) {
        id = crypto.randomUUID();
        localStorage.setItem('kk-visitor', id);
      }
      return id;
    } catch {
      fallbackVisitor ||= crypto.randomUUID();
      return fallbackVisitor;
    }
  }

  function format(count) {
    if (count < 1000) return String(count);
    return `${(count / 1000).toFixed(count < 10000 ? 1 : 0).replace(/\.0$/, '')}k`;
  }

  function render() {
    document.querySelectorAll('[data-like-entry]').forEach((button) => {
      const entry = state[button.dataset.likeEntry];
      if (!entry) return;
      button.classList.toggle('is-liked', entry.liked);
      button.setAttribute('aria-pressed', String(entry.liked));
      button.setAttribute('aria-label', `${entry.liked ? 'Unlike' : 'Like'}${entry.likes ? ` (${entry.likes} like${entry.likes === 1 ? '' : 's'})` : ''}`);
      const count = button.querySelector('[data-like-count]');
      const text = entry.likes > 0 ? format(entry.likes) : '';
      if (count && count.textContent !== text) count.textContent = text;
    });
    document.querySelectorAll('[data-plays-entry]').forEach((label) => {
      const entry = state[label.dataset.playsEntry];
      const show = entry && entry.plays >= PLAYS_SHOWN_FROM;
      if (label.hidden === show) label.hidden = !show;
      const text = show ? `${format(entry.plays)} plays` : '';
      if (show && label.textContent !== text) label.textContent = text;
    });
  }

  async function call(path, options) {
    const response = await fetch(`${api}${path}`, options);
    if (!response.ok) throw new Error(`Community stats: ${response.status}`);
    return response.json();
  }

  async function loadMissing() {
    const ids = new Set();
    document.querySelectorAll('[data-like-entry], [data-plays-entry]').forEach((element) => {
      const id = element.dataset.likeEntry || element.dataset.playsEntry;
      if (id && !requested.has(id)) ids.add(id);
    });
    if (!ids.size) return render();
    ids.forEach((id) => requested.add(id));
    try {
      const { counts } = await call(`/api/stats?ids=${[...ids].join(',')}&visitor=${encodeURIComponent(visitorId())}`);
      Object.assign(state, counts);
    } catch (error) {
      console.warn(error);
    }
    render();
  }

  function details(element) {
    return {
      title: element.dataset.entryTitle || '',
      path: element.dataset.entryPath || window.location.pathname,
    };
  }

  async function toggleLike(button) {
    const id = button.dataset.likeEntry;
    const before = state[id] || { likes: 0, plays: 0, liked: false };
    const liked = !before.liked;
    // Update straight away so the tap feels instant; put it back if saving fails.
    state[id] = { ...before, liked, likes: Math.max(0, before.likes + (liked ? 1 : -1)) };
    render();
    button.classList.remove('is-popping');
    if (liked) requestAnimationFrame(() => button.classList.add('is-popping'));
    try {
      state[id] = await call('/api/like', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ entry: id, visitor: visitorId(), liked, ...details(button) }),
      });
    } catch (error) {
      console.warn(error);
      state[id] = before;
    }
    render();
  }

  async function recordPlay(box) {
    const id = box.dataset.playEntry;
    const day = new Date().toISOString().slice(0, 10);
    const key = `kk-played:${id}:${day}`;
    try {
      if (localStorage.getItem(key)) return;
      localStorage.setItem(key, '1');
    } catch {
      /* private browsing: the service still counts once per day */
    }
    try {
      state[id] = await call('/api/play', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ entry: id, visitor: visitorId(), ...details(box) }),
      });
      render();
    } catch (error) {
      console.warn(error);
    }
  }

  document.addEventListener('click', (event) => {
    const button = event.target.closest?.('[data-like-entry]');
    if (!button) return;
    event.preventDefault();
    event.stopPropagation(); // don't also open the build viewer
    toggleLike(button);
  });

  document.addEventListener(
    'play',
    (event) => {
      const box = event.target.closest?.('[data-play-entry]');
      if (box) recordPlay(box);
    },
    true
  );

  // The build viewer adds its details panel after the page loads; pick up any new buttons.
  // Only reacts to newly added buttons/labels, never to this script's own count updates.
  const selector = '[data-like-entry], [data-plays-entry]';
  new MutationObserver((mutations) => {
    const added = mutations.some((mutation) =>
      [...mutation.addedNodes].some((node) => node.nodeType === 1 && (node.matches(selector) || node.querySelector(selector)))
    );
    if (added) loadMissing();
  }).observe(document.documentElement, { childList: true, subtree: true });

  window.kkCommunityStats = { refresh: loadMissing };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', loadMissing);
  else loadMissing();
})();
